import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/site/Reveal";
import SectionHead from "@/components/site/SectionHead";
import CTASection from "@/components/site/CTASection";
import { useLang } from "@/i18n";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const MarketingBranding = () => {
  const { d } = useLang();
  const p = d.mb;
  return (
    <div data-testid="marketing-branding-page">
      <section className="bg-paper py-20 lg:py-28" data-testid="mb-hero">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-accent"
          >
            <span className="h-px w-10 bg-accent" /> {p.heroLabel}
          </motion.p>
          <h1 className="max-w-4xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="mb-headline">
            <MaskedLine delay={0.1}>{p.heroL1}</MaskedLine>
            <MaskedLine delay={0.25}><span className="italic text-accent">{p.heroL2}</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-mutedink sm:text-lg"
          >
            {p.sub}
          </motion.p>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="mb-aree">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={p.aree.overline} title={p.aree.title} lead={p.aree.lead} />
          <div className="mt-16 grid gap-px overflow-hidden rounded-md bg-ink/10 md:grid-cols-2">
            {p.aree.areas.map((a, i) => (
              <Reveal key={a.n} delay={i * 0.08} className="min-w-0 bg-white p-8 transition-colors duration-300 hover:bg-paper lg:p-12" data-testid={`mb-area-${a.n}`}>
                <div className="flex items-baseline gap-4">
                  <p className="font-mono text-sm text-accent">{a.n}</p>
                  <h3 className="font-serif text-3xl text-ink">{a.title}</h3>
                </div>
                <ul className="mt-7 grid gap-2.5 sm:grid-cols-2">
                  {a.items.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm text-ink/80">
                      <span className="h-1 w-1 shrink-0 rotate-45 bg-accent" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper py-24 lg:py-32" data-testid="mb-packages">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={p.packages.overline} title={p.packages.title} lead={p.packages.lead} />
          <div className="mt-16 grid gap-8 lg:grid-cols-3">
            {p.packages.list.map((pkg, i) => (
              <Reveal
                key={pkg.name}
                delay={i * 0.1}
                className="flex flex-col rounded-md border border-ink/12 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent lg:p-10"
                data-testid={`package-${slug(pkg.name)}`}
              >
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{pkg.name}</p>
                <p className="mt-2 font-serif text-2xl text-ink">{pkg.role}</p>
                <div className="mt-5 border-y border-ink/10 py-5">
                  <p className="whitespace-nowrap font-serif text-3xl tracking-tight text-ink sm:text-4xl">
                    {pkg.price} <span className="font-sans text-sm font-normal text-mutedink">{pkg.vat}</span>
                  </p>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{pkg.duration}</p>
                  {pkg.total && (
                    <p className="mt-2 text-xs font-medium text-ink/70" data-testid={`package-${slug(pkg.name)}-total`}>{pkg.total}</p>
                  )}
                </div>
                <p className="mt-5 text-sm leading-relaxed text-mutedink">{pkg.problem}</p>
                <ul className="mt-6 flex-1 space-y-3 border-t border-ink/10 pt-6 text-sm">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex gap-3">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={2} />
                      <span className="text-ink/85">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/contatti?area=${slug(pkg.name)}`}
                  data-testid={`package-${slug(pkg.name)}-cta`}
                  className="mt-8 inline-flex items-center justify-center gap-3 rounded-md bg-accent px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark"
                >
                  {p.packages.cta} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to={`/pacchetti/${slug(pkg.name)}`}
                  data-testid={`package-${slug(pkg.name)}-detail`}
                  className="mt-3 inline-flex items-center justify-center gap-2 rounded-md border border-ink/20 px-7 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:border-accent hover:text-accent"
                >
                  {d.pkg.common.detailCta} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <p className="text-sm text-mutedink">{p.packages.note}</p>
          </Reveal>

          <Reveal delay={0.1} className="mt-8">
            <div className="flex flex-col gap-6 rounded-md border border-accent/40 bg-accent-light p-8 sm:flex-row sm:items-center sm:justify-between lg:p-10" data-testid="mb-checkup-band">
              <div className="max-w-2xl">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{p.packages.checkupBand.label}</p>
                  <p className="whitespace-nowrap font-serif text-3xl text-ink">{p.packages.checkupBand.price}</p>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-ink/75">{p.packages.checkupBand.body}</p>
              </div>
              <Link
                to="/check-up"
                data-testid="mb-checkup-band-cta"
                className="inline-flex shrink-0 items-center gap-3 self-start rounded-md bg-accent px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark sm:self-auto"
              >
                {p.packages.checkupBand.cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="mb-compare">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={p.packages.compare.overline} title={p.packages.compare.title} lead={p.packages.compare.lead} />
          <div className="mt-16 grid gap-px overflow-hidden rounded-md bg-ink/10 lg:grid-cols-3" data-testid="compare-grid">
            {p.packages.list.map((pkg, ci) => (
              <Reveal key={pkg.name} delay={ci * 0.08} className="bg-white p-8 lg:p-10" data-testid={`compare-${slug(pkg.name)}`}>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{pkg.name}</p>
                <p className="mt-1 font-serif text-2xl text-ink">{pkg.role}</p>
                <dl className="mt-6 divide-y divide-ink/10 border-y border-ink/10">
                  {p.packages.compare.criteria.map((c) => (
                    <div key={c.label} className="grid grid-cols-[1fr_1.2fr] gap-4 py-3.5">
                      <dt className="text-xs font-medium uppercase tracking-[0.08em] text-mutedink">{c.label}</dt>
                      <dd className="text-sm text-ink">{c.values[ci]}</dd>
                    </div>
                  ))}
                </dl>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="mb-proof">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={p.proof.overline} title={p.proof.title} lead={p.proof.lead} />
          <div className="mt-16 border-t border-ink/10">
            {p.proof.items.map((e, i) => (
              <Reveal key={e.company} delay={i * 0.05} className="grid gap-6 border-b border-ink/10 py-10 lg:grid-cols-12 lg:gap-10 lg:py-12" data-testid={`mb-proof-${i}`}>
                <div className="lg:col-span-4">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">{e.dates}</p>
                  <h3 className="mt-3 font-serif text-2xl leading-snug text-ink sm:text-3xl">{e.role}</h3>
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{e.company}</p>
                </div>
                <div className="lg:col-span-8">
                  <p className="max-w-3xl text-sm leading-relaxed text-mutedink sm:text-base">{e.body}</p>
                  {e.quote && (
                    <figure className="mt-6 max-w-2xl rounded-md border-l-2 border-accent bg-paper p-6" data-testid={`mb-proof-quote-${i}`}>
                      <blockquote className="font-serif text-lg italic leading-relaxed text-ink">{e.quote.text}</blockquote>
                      <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-accent">{e.quote.author}</figcaption>
                    </figure>
                  )}
                  {e.brands && e.brands.length > 0 && (
                    <div className="mt-6">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink">{p.proof.brandsLabel}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {e.brands.map((b) => (
                          <span key={b} className="rounded-sm border border-ink/15 bg-paper px-3 py-1.5 text-xs text-ink/80">{b}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {e.sites && e.sites.length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {e.sites.map((s, si) => (
                        <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`mb-proof-${i}-site-${si}`} className="inline-flex items-center gap-1.5 rounded-md border border-ink/20 bg-paper px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/80 transition-colors duration-300 hover:border-accent hover:text-accent">
                          <ArrowUpRight className="h-3 w-3 text-accent" /> {s.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection interest="marketing-branding" />
    </div>
  );
};

export default MarketingBranding;
